export type ColumnCount = 1 | 2 | 3 | 4 | 5 | 6;

export type ColumnBorderStyle = "none" | "all" | "top-bottom";

export type ColumnVariant = "blog" | "landing";

export type ColumnVerticalAlign = "top" | "center";

const COLUMN_GRID_CLASS: Record<ColumnCount, string> = {
  1: "",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
  5: "md:grid-cols-5",
  6: "md:grid-cols-6",
};

const LANDING_COLUMN_GRID_CLASS: Record<ColumnCount, string> = {
  1: "",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
  5: "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5",
  6: "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6",
};

export function normalizeColumnBorderStyle(value: unknown): ColumnBorderStyle {
  if (value === true || value === "all") {
    return "all";
  }
  if (value === "top-bottom") {
    return "top-bottom";
  }
  return "none";
}

export function getColumnGridClassName(
  columnCount: ColumnCount,
  variant: ColumnVariant = "blog",
): string {
  if (variant === "landing") {
    return LANDING_COLUMN_GRID_CLASS[columnCount];
  }
  return COLUMN_GRID_CLASS[columnCount];
}

export function normalizeColumnVerticalAlign(
  value: unknown,
): ColumnVerticalAlign {
  return value === "center" ? "center" : "top";
}

export function getColumnVerticalAlignClassName(
  verticalAlign: ColumnVerticalAlign,
  variant: ColumnVariant = "blog",
): string {
  if (variant !== "landing" || verticalAlign !== "center") {
    return "";
  }

  return "items-center";
}

export function getColumnContainerClassName(
  columnCount: ColumnCount,
  borderStyle: ColumnBorderStyle,
  variant: ColumnVariant = "blog",
  verticalAlign: ColumnVerticalAlign = "top",
): string {
  if (variant === "landing") {
    const base = `grid grid-cols-1 gap-4 ${getColumnGridClassName(columnCount, "landing")} ${getColumnVerticalAlignClassName(verticalAlign, "landing")}`.trim();
    return base;
  }

  const base = `not-prose my-4 grid grid-cols-1 ${getColumnGridClassName(columnCount, "blog")}`.trim();

  if (borderStyle === "all") {
    return `${base} gap-0 border-y border-gail-gray/30`;
  }

  if (borderStyle === "top-bottom") {
    return `${base} gap-8 border-y border-gail-gray/30 px-6 py-8 md:px-8`;
  }

  return `${base} gap-8`;
}

export function getColumnCellClassName(
  index: number,
  borderStyle: ColumnBorderStyle,
  variant: ColumnVariant = "blog",
  options?: { hasSectionBackground?: boolean },
): string {
  if (variant === "landing") {
    const elevated =
      options?.hasSectionBackground && index === 1 ? "relative z-10" : "";
    return ["min-w-0 flex flex-col", elevated].filter(Boolean).join(" ");
  }

  const base =
    "min-w-0 flex flex-col gap-2 prose prose-headings:mt-0 max-w-none";

  if (borderStyle !== "all") {
    return base;
  }

  const divider =
    index > 0
      ? "border-t border-gail-gray/30 md:border-t-0 md:border-l"
      : "";

  return `${base} px-6 py-8 md:px-8 md:py-10 ${divider}`.trim();
}

export function clampColumnCount(count: number): ColumnCount {
  const normalized = Number.isFinite(count) ? Math.trunc(count) : 1;
  if (normalized <= 1) return 1;
  if (normalized >= 6) return 6;
  return normalized as ColumnCount;
}

export const COLUMN_SLOT_KEYS = [
  "column1",
  "column2",
  "column3",
  "column4",
  "column5",
  "column6",
] as const;

export type ColumnSlotKey = (typeof COLUMN_SLOT_KEYS)[number];
